package com.snakesworld.game.network

import com.snakesworld.game.engine.LeaderboardEntry
import kotlinx.coroutines.flow.StateFlow
import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder

/**
 * Custom serializer encoding Long values as Double numbers.
 * In Kotlin/JS, Long is a composite class object ({low_, high_}) which Firebase JS SDK rejects
 * with "Unsupported field value: a custom Long object". Encoding as Double yields a native JS number
 * and conforms to Firestore rules ("data.ts is number", "data.c is number").
 */
object LongAsDoubleSerializer : KSerializer<Long> {
    override val descriptor: SerialDescriptor =
        PrimitiveSerialDescriptor("LongAsDouble", PrimitiveKind.DOUBLE)

    override fun serialize(encoder: Encoder, value: Long) {
        encoder.encodeDouble(value.toDouble())
    }

    override fun deserialize(decoder: Decoder): Long {
        return try {
            decoder.decodeDouble().toLong()
        } catch (_: Throwable) {
            decoder.decodeLong()
        }
    }
}

/**
 * Real-time network connection health states.
 */
enum class ConnectionStatus {
    Connected,
    Unstable,
    Disconnected,
}

/**
 * Compact network payload broadcasting snake head kinematics.
 */
@Serializable
data class SnakeNetworkState(
    val id: String = "",
    val x: Float = 0f,
    val y: Float = 0f,
    val a: Float = 0f,
    val s: Float = 0f,
    val b: Boolean = false,
    val m: Float = 0f,
    val n: String = "",
    val sk: String = "",
    @Serializable(with = LongAsDoubleSerializer::class)
    val ts: Long = 0L,
)

/**
 * Compact network payload for food pellets.
 */
@Serializable
data class PelletNetworkState(
    val id: String = "",
    val x: Float = 0f,
    val y: Float = 0f,
    val m: Float = 1f,
    @Serializable(with = LongAsDoubleSerializer::class)
    val c: Long = 0L,
    val cp: Boolean = false,
    val bt: Boolean = false,
    /** Uid of the snake that scored the kill, when this pellet is a death orb. Empty otherwise. */
    val kb: String = "",
)

/**
 * Persistent pilot profile with all-time arcade records.
 * Multiplayer personal bests (`best*` / `gamesPlayed`) are separate from single-player
 * personal bests (`spBest*` / `spGamesPlayed`). The public `/leaderboards`
 * collection is multiplayer-only.
 */
@Serializable
data class UserProfile(
    val uid: String = "",
    val nickname: String = "PLAYER_01",
    // Multiplayer personal stats
    val gamesPlayed: Int = 0,
    val bestScore: Int = 0,
    val bestKills: Int = 0,
    val bestLength: Int = 0,
    // Single-player personal stats
    val spGamesPlayed: Int = 0,
    val spBestScore: Int = 0,
    val spBestKills: Int = 0,
    val spBestLength: Int = 0,
    val markDelete: Boolean = false,
    /** Uids whose nicknames this player has chosen to hide. Capped by the client. */
    val blockedIds: List<String> = emptyList(),
)

/**
 * One report of an abusive nickname. Document id is `{reporterId}_{targetId}`
 * so a repeat report overwrites the same document instead of adding writes.
 */
@Serializable
data class PlayerReport(
    val reporterId: String = "",
    val targetId: String = "",
    val targetName: String = "",
    @Serializable(with = LongAsDoubleSerializer::class)
    val createdAt: Long = 0L,
)

/**
 * Why the local snake died. Distinguishes wall, self-hit, and opponent kills
 * so game-over copy is not inferred from a missing killer name.
 */
@Serializable
enum class DeathCause {
    ArenaBoundary,
    SelfCollision,
    Opponent,
}

/**
 * Summary record produced at the conclusion of a match.
 */
@Serializable
data class SessionResult(
    val score: Int = 0,
    val kills: Int = 0,
    val length: Int = 0,
    @Serializable(with = LongAsDoubleSerializer::class)
    val survivedSeconds: Long = 0L,
    val killerName: String? = null,
    val killerId: String? = null,
    val deathCause: DeathCause = DeathCause.ArenaBoundary,
)

/**
 * Firebase authentication lifecycle states.
 */
sealed interface AuthState {
    data object Uninitialized : AuthState
    data object Initializing : AuthState
    data class Authenticated(val uid: String) : AuthState
    data class Error(val message: String) : AuthState
}

/**
 * Main networking interface providing real-time synchronization and persistence.
 */
interface MultiplayerRepository {
    val authState: StateFlow<AuthState>
    val connectionStatus: StateFlow<ConnectionStatus>
    val snakes: StateFlow<Map<String, SnakeNetworkState>>
    val pellets: StateFlow<Map<String, PelletNetworkState>>

    suspend fun initializeAndSignInAnonymously(): Result<String>
    /**
     * Sign out the current session and reset [authState]. Call only after account documents
     * have been deleted, or from [clearUserData] after those deletes succeed.
     */
    suspend fun signOut(): Result<Unit>
    suspend fun joinArena(profile: UserProfile, skinId: String): String
    suspend fun leaveArena()
    suspend fun broadcastHeadState(state: SnakeNetworkState)
    suspend fun spawnPellets(pellets: List<PelletNetworkState>)
    suspend fun consumePellets(pelletIds: List<String>)
    suspend fun submitDemise(victimId: String, deathDrops: List<PelletNetworkState>)
    /**
     * Writes a single `reports/{reporterUid}_{targetId}` document. No listener.
     * Repeat reports overwrite that document.
     */
    suspend fun reportPlayer(targetId: String, targetName: String)
    suspend fun getLeaderboard(): List<LeaderboardEntry>
    /** Increment games-played immediately when the player enters a match. */
    suspend fun recordGameStarted(isSinglePlayer: Boolean)
    /** Update personal bests (and SP history) on lose/quit — does not bump games-played. */
    suspend fun submitHighScore(score: Int, kills: Int, skinId: String, length: Int = 0, isSinglePlayer: Boolean = false)
    suspend fun getUserProfile(uid: String): UserProfile?
    suspend fun saveUserProfile(profile: UserProfile)
    /**
     * Permanently erase the account documents for [userId] (profile, leaderboard, arena snake)
     * while the user is still authenticated so Firestore owner rules succeed, then sign out.
     * Must not sign out if the user document delete fails.
     */
    suspend fun clearUserData(userId: String): Result<Unit>
}
